<template>
 <div
  ref="note-card-ref"
  class="u-p3">
    <div>
      <div
        :class="[
          'l-flex-grid--no-wrap',
          'l-flex-grid--h-space-between',
          'u-mb2',
          'u-text--xs',
          'u-text--bold',
          'u-text--light',
          'u-text--uppercase'
        ]">
        <div>
          <slot name="note-breadcrumb">
            {{note_type ? note_type.name : ''}}
            <fa-icon
              v-if="note_type"
              class="u-ml2 u-mr2"
              :icon="['fas', 'caret-right']"
            />
            {{reference.reference_label}}
          </slot>
        </div>
        <div
          v-if="is_locked"
        >
          <el-tooltip
            content="This note is locked: no edits or deletions are allowed."
            effect="dark"
            placement="top"
          >
            <fa-icon
              v-test-id="'lock-icon'"
              class="u-mr0"
              :icon="['fas', 'lock']"
            />
          </el-tooltip>
        </div>
      </div>
      <div class="l-flex-grid--no-wrap l-flex-grid--v-center u-mb2">
        <slot
          name="note-title"
          :title="title">
            <h1
              @click="$emit('title-click')"
              :class="[
                'u-m0',
                `${linkableTitle ? 'u-link' : ''}`,
                'u-text--md',
                'u-text--bold'
              ]"
              v-data-element:title-link="{ dataElement }"
            >
              {{title}}
            </h1>
        </slot>
        <div class="u-ml-auto">
          <slot name="header-icon"/>
          <note-card-actions-popover
            v-if="showActions"
            :editable="editable"
            :deletable="deletable"
            @edit-note-click="$emit('edit-note-click')"
            @delete-note-click="$emit('delete-note-click')"
          />
        </div>
      </div>
      <div>
        <slot name="note-subtitle" />
      </div>
    </div>
    <note-editor
      @text-truncated="wasTruncationApplied = true"
      :truncate-content="shouldTruncateBody"
      :shouldShowMenuBar="false"
      :editable="false"
      :value="body"
    />
    <div
      v-if="wasTruncationApplied"
    >
      <el-button
        @click="toggleTruncateMode"
        block
        type="text"
        size="small"
        :data-element="dataElement ? `${dataElement}-view` : dataElement"
      >
        <fa-icon
          class="u-mr1"
          :icon="['fas', `${shouldTruncateBody ? 'angle-down' : 'angle-up'}`]"
        />
        <span>
          View {{shouldTruncateBody ? 'more' : 'less'}}
        </span>
      </el-button>
    </div>
    <div
      :class="[
        'u-mt3',
        'u-text--sm',
        'l-flex-grid--no-wrap',
        'c-inline-status__secondary-label'
      ]">
      <div>
        <span>By: {{creator}} - {{dateAddedTimestamp}}</span>
      </div>
    </div>
    <note-tags-list
      class="u-mt3"
      :list="tags" />
    <attachments-list :list="attachments" />
    <el-loading-wrapper
      v-if="history.length || historyLoading"
      tag="div"
      :loading="historyLoading"
      loading-text="Loading note history ...">
      <note-history-list :list="history" />
    </el-loading-wrapper>
  </div>
</template>

<script>
import NoteEditor from '#/notes/components/NoteEditor'
import NoteTagsList from '#/notes/components/NoteTagsList'
import AttachmentsList from '#/notes/components/AttachmentsList'
import NoteHistoryList from '#/notes/components/NoteHistoryList'
import NoteCardActionsPopover from '#/notes/components/NoteCardActionsPopover'
import dateUtils from '#/notes/utilities/date'

export default {
  name: 'NoteCard',
  data () {
    return {
      wasTruncationApplied: false,
      shouldTruncateBody: this.truncateBody
    }
  },
  props: {
    title: {
      type: String,
      required: true
    },
    body: {
      type: String,
      required: true
    },
    date_added: {
      type: [Date, String],
      default: null
    },
    creator: {
      type: String,
      required: true
    },
    attachments: {
      type: Array,
      default: () => []
    },
    history: {
      type: Array,
      default: () => []
    },
    historyLoading: {
      type: Boolean,
      default: false
    },
    is_locked: {
      type: Boolean,
      default: false
    },
    reference: {
      type: Object,
      required: true
    },
    note_type: {
      type: Object
    },
    editable: {
      type: Boolean,
      default: false
    },
    deletable: {
      type: Boolean,
      default: false
    },
    showActions: {
      type: Boolean,
      default: false
    },
    truncateBody: {
      type: Boolean,
      default: false
    },
    tags: {
      type: Array,
      default: () => []
    },
    linkableTitle: {
      type: Boolean,
      default: false
    },
    dataElement: String
  },
  components: {
    NoteEditor,
    NoteTagsList,
    AttachmentsList,
    NoteHistoryList,
    NoteCardActionsPopover
  },
  watch: {
    truncateBody: {
      handler () {
        this.shouldTruncateBody = this.truncateBody
      },
      immediate: true
    }
  },
  computed: {
    dateAddedTimestamp () {
      const dateAdded = dateUtils.getDateTz(this.date_added)
      return `${dateAdded.format('MM/DD/YY hh:mm A z')}`
    }
  },
  methods: {
    toggleTruncateMode () {
      this.shouldTruncateBody = !this.shouldTruncateBody
      // keep the note card in view
      this.$nextTick(() => {
        this.$refs['note-card-ref'].scrollIntoView()
      })
    }
  }
}
</script>
